import "./bootstrap";
import "../css/app.css";

import * as Sentry from "@sentry/vue";

import { createApp, h } from "vue";
import { createInertiaApp, router } from "@inertiajs/vue3";
import { resolvePageComponent } from "laravel-vite-plugin/inertia-helpers";
import { ZiggyVue } from "../../vendor/tightenco/ziggy/dist/vue.m";

const appName = import.meta.env.VITE_APP_NAME || "RoadbookRally";

createInertiaApp({
    progress: {
        color: "#4B5563",
    },
    title: (title) => `${title} - ${appName}`,
    resolve: (name) => resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob("./Pages/**/*.vue")),
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => h(App, props) });

        if (import.meta.env.VITE_APP_ENV === "production") {
            // Noise: network / third-party failures we can neither fix nor act on.
            const excludedErrorsContaining = [
                "Network Error", // AxiosError: Network Error
                "Failed to fetch https://api.mapbox.com",
                "Failed to fetch", // mapbox-gl tile fetch failures on flaky mobile networks
                "NetworkError when attempting to fetch resource", // Firefox wording of "Failed to fetch" (mapbox-gl tile fetches)
                "Load failed", // Safari/iOS WebKit wording of "Failed to fetch"; mapbox-gl console.error()s it when navigation aborts in-flight tile fetches
                "error loading dynamically imported module", // TypeError: error loading dynamically imported module
                "Unable to preload CSS for", // Error: Unable to preload CSS for
                "canceled", // CanceledError: canceled
                "Failed to initialize WebGL", // Error: Failed to initialize WebGL
                "Cannot read properties of undefined (reading 'send')", // Cannot read properties of undefined (reading 'send') at https://roadbookrally.com/build/assets/mapbox",
                "Can't find variable: gmo",
                "Object Not Found Matching Id:", // Outlook SafeLinks / Defender link scanner (Azure IPs), any Id — not our code
                "No error message", // https://binde.sentry.io/issues/6448911587/?project=4505639700660224&query=is%3Aunresolved%20issue.priority%3A%5Bhigh%2C%20medium%5D&referrer=issue-stream&stream_index=0
                "undefined is not an object (evaluating 'a.J')", // https://binde.sentry.io/issues/5987178731/?project=4505639700660224&query=is%3Aunresolved%20issue.priority%3A%5Bhigh%2C%20medium%5D&referrer=issue-stream&stream_index=1
                "Java object is gone", // Facebook in-app browser's own injected iabjs:// script, not our code
                "updateAndCleanExtractedContent", // Edge iOS injected page-extraction script console.error()s its own stack (ROADBOOKRALLY-FRONTEND-RP), not our code
                "this.WrappedBody.rootNode", // same Edge iOS extraction script, its "Error during extraction" console.error (ROADBOOKRALLY-FRONTEND-RQ)
                "window.webkit.messageHandlers", // Meta iOS in-app browser injected sendDataToNative(), not our code
                "wptagent_message:", // WebPageTest bot agent console.debug()s its perf entries (ROADBOOKRALLY-FRONTEND-JC, -RN), not our code
                "Importing a module script failed.", // https://binde.sentry.io/issues/5987178731/?project=4505639700660224&query=is%3Aunresolved%20issue.priority%3A%5Bhigh%2C%20medium%5D&referrer=issue-stream&stream_index=1
            ];

            const integrations = [
                Sentry.replayIntegration({
                    maskAllText: false,
                    blockAllMedia: false,
                    beforeErrorSampling: (event) => {
                        const errorMessage = event.exception?.values?.[0]?.value;

                        // Skip sampling if the error message includes any of the excluded substrings
                        return !excludedErrorsContaining.some((substring) => errorMessage?.includes(substring));
                    },
                }),
                Sentry.extraErrorDataIntegration(),
                Sentry.browserTracingIntegration({
                    // Mapbox GL fetches tiles/glyphs in parallel batches; Sentry flags them as N+1 API calls (ROADBOOKRALLY-FRONTEND-QK).
                    shouldCreateSpanForRequest: (url) => !url.includes("mapbox.com"),
                }),
                Sentry.captureConsoleIntegration({
                    levels: ["error", "debug"],
                }),
            ];

            const excludedFeedbackButtonRoutes = [
                "event.public.start-list-clock",
                "event.public.player",
                "event.public.replay",
                "track.public.player",
                "track.public.replay",
                "event.public.live",
                "event.public.live-tracking",
                "track.public.map",
                "track.public.information",
            ];

            integrations.push(
                Sentry.feedbackIntegration({
                    autoInject: false,
                    buttonLabel: "Feedback",
                    triggerLabel: "Feedback",
                    showName: false,
                    formTitle: "Report a bug or share your feedback",
                    submitButtonLabel: "Send",
                }),
            );

            Sentry.init({
                app, // variable name must be "app"
                dsn: import.meta.env.VITE_SENTRY_LARAVEL_DSN,
                integrations: integrations,

                // Set tracesSampleRate to 1.0 to capture 100%
                // of transactions for performance monitoring.
                // We recommend adjusting this value in production
                tracesSampleRate: 0.5,

                // Session Replay
                replaysSessionSampleRate: 0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
                replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

                ignoreErrors: [
                    ...excludedErrorsContaining,
                    // Random plugins/extensions
                    "top.GLOBALS",
                    // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
                    "originalCreateNotification",
                    "canvas.contentDocument",
                    "MyApp_RemoveAllHighlights",
                    "http://tt.epicplay.com",
                    "Can't find variable: ZiteReader",
                    "jigsaw is not defined",
                    "ComboSearch is not defined",
                    "http://loading.retry.widdit.com/",
                    "atomicFindClose",
                    // Facebook borked
                    "fb_xd_fragment",
                    // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
                    // reduce this. (thanks @acdha)
                    // See http://stackoverflow.com/questions/4113268
                    "bmi_SafeAddOnload",
                    "EBCallBackMessageReceived",
                    // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
                    "conduitPage",
                ],
                denyUrls: [
                    // RBR
                    /posthog\.com/i,
                    /eu.i.posthog\.com/i,
                    /mapbox\.com/i,
                    /api.mapbox\.com/i,
                    /events.mapbox\.com/i,
                    /tawk\.to/i,
                    /va.tawk\.to/i,
                    /embed.tawk\.to/i,
                    // Facebook flakiness
                    /graph\.facebook\.com/i,
                    // Facebook blocked
                    /connect\.facebook\.net\/en_US\/all\.js/i,
                    // Facebook in-app browser injected scripts
                    /^iabjs:\/\//i,
                    // Woopra flakiness
                    /eatdifferent\.com\.woopra-ns\.com/i,
                    /static\.woopra\.com\/js\/woopra\.js/i,
                    // Chrome extensions
                    /extensions\//i,
                    /^chrome:\/\//i,
                    /^chrome-extension:\/\//i,
                    // Other plugins
                    /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
                    /webappstoolbarba\.texthelp\.com\//i,
                    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
                ],
            });

            if (props.initialPage.props.user) {
                // console.log("props.initialPage.props.user", props.initialPage.props.user);
                Sentry.setUser({
                    // fullName: props.initialPage.props.user.name,
                    // name: props.initialPage.props.user.name,
                    email: props.initialPage.props.user.email,
                    // id: props.initialPage.props.user.id,
                });
            }

            // Dynamically show/hide feedback widget based on route
            let feedbackWidget = null;

            // eslint-disable-next-line no-inner-declarations
            function updateFeedbackWidget() {
                const currentRoute = route().current();
                const isExcluded = excludedFeedbackButtonRoutes.some((r) => currentRoute?.startsWith(r));

                if (isExcluded && feedbackWidget) {
                    feedbackWidget.el?.remove();
                    feedbackWidget = null;
                } else if (!isExcluded && !feedbackWidget) {
                    const feedback = Sentry.getFeedback();
                    if (feedback) {
                        feedbackWidget = feedback.createWidget();
                    }
                }
            }

            updateFeedbackWidget();
            router.on("navigate", () => {
                updateFeedbackWidget();
            });
        }

        return app.use(plugin).use(ZiggyVue).mount(el);
    },
});
